import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import {
    ApiError,
    type JoinedOutreachRead,
} from '@libraries/typescript/api_client';

type ScheduleConflictAssignment = {
    readonly caregiverId: string;
    readonly requestedShiftId: string;
    readonly conflictingShiftId: string;
    readonly patientName: string;
    readonly shiftStartTime: string;
    readonly shiftEndTime: string;
};

export type OutreachScheduleConflict = {
    readonly acknowledgedConflicts: Readonly<Record<string, Array<string>>>;
    readonly assignments: ReadonlyArray<ScheduleConflictAssignment>;
    readonly caregiverNames: Readonly<Record<string, string>>;
};

export function parseOutreachScheduleConflict(
    error: unknown,
): OutreachScheduleConflict | null {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }
    const detail = (error.body as { readonly detail?: unknown } | undefined)
        ?.detail;
    if (typeof detail !== 'object' || detail === null) {
        return null;
    }
    const detailRecord = detail as Readonly<Record<string, unknown>>;
    const rawConflicts = detailRecord['conflicts'];
    if (typeof rawConflicts !== 'object' || rawConflicts === null) {
        return null;
    }

    const acknowledgedConflicts: Record<string, Array<string>> = {};
    for (const [shiftId, rawCaregiverIds] of Object.entries(
        rawConflicts as Readonly<Record<string, unknown>>,
    )) {
        if (!Array.isArray(rawCaregiverIds)) {
            continue;
        }
        for (const caregiverId of rawCaregiverIds) {
            if (typeof caregiverId !== 'string') {
                continue;
            }
            (acknowledgedConflicts[caregiverId] ??= []).push(shiftId);
        }
    }
    if (Object.keys(acknowledgedConflicts).length === 0) {
        return null;
    }

    const rawAssignments = detailRecord['conflicting_assignments'];
    const assignments = Array.isArray(rawAssignments)
        ? rawAssignments.flatMap((rawAssignment) => {
              if (typeof rawAssignment !== 'object' || rawAssignment === null) {
                  return [];
              }
              const assignment = rawAssignment as Readonly<
                  Record<string, unknown>
              >;
              const caregiverId = assignment['caregiver_id'];
              const requestedShiftId = assignment['requested_shift_id'];
              const conflictingShiftId = assignment['conflicting_shift_id'];
              const patientName = assignment['patient_name'];
              const shiftStartTime = assignment['shift_start_time'];
              const shiftEndTime = assignment['shift_end_time'];
              if (
                  typeof caregiverId !== 'string' ||
                  typeof requestedShiftId !== 'string' ||
                  typeof conflictingShiftId !== 'string' ||
                  typeof patientName !== 'string' ||
                  typeof shiftStartTime !== 'string' ||
                  typeof shiftEndTime !== 'string'
              ) {
                  return [];
              }
              return [
                  {
                      caregiverId,
                      requestedShiftId,
                      conflictingShiftId,
                      patientName,
                      shiftStartTime,
                      shiftEndTime,
                  },
              ];
          })
        : [];
    const rawCaregiverNames = detailRecord['caregiver_names'];
    const caregiverNames =
        typeof rawCaregiverNames === 'object' && rawCaregiverNames !== null
            ? Object.fromEntries(
                  Object.entries(
                      rawCaregiverNames as Readonly<Record<string, unknown>>,
                  ).filter(
                      (entry): entry is [string, string] =>
                          typeof entry[1] === 'string',
                  ),
              )
            : {};
    return { acknowledgedConflicts, assignments, caregiverNames };
}

export function OutreachScheduleConflictDialog({
    conflict,
    shifts,
    timezone,
    isSubmitting,
    onCancel,
    onConfirm,
}: {
    readonly conflict: OutreachScheduleConflict | null;
    readonly shifts: JoinedOutreachRead['shifts'];
    readonly timezone: string | undefined;
    readonly isSubmitting: boolean;
    readonly onCancel: () => void;
    readonly onConfirm: (
        acknowledgedConflicts: Record<string, Array<string>>,
    ) => void;
}) {
    const displayTimezone = timezone ?? 'UTC';
    return (
        <Dialog
            open={conflict !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
        >
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Start despite schedule conflicts?</DialogTitle>
                    <DialogDescription>
                        These caregivers have overlapping assigned shifts.
                        Review the exact conflicts before starting outreach.
                    </DialogDescription>
                </DialogHeader>
                {conflict && (
                    <div className="max-h-80 space-y-4 overflow-y-auto text-sm">
                        {Object.entries(conflict.acknowledgedConflicts).map(
                            ([caregiverId, shiftIds]) => (
                                <div key={caregiverId} className="space-y-2">
                                    <div className="font-medium">
                                        {conflict.caregiverNames[caregiverId] ??
                                            'Caregiver'}
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground">
                                            Outreach shifts
                                        </div>
                                        <ul className="list-disc pl-5">
                                            {shifts
                                                .filter((shift) =>
                                                    shiftIds.includes(shift.id),
                                                )
                                                .map((shift) => (
                                                    <li key={shift.id}>
                                                        {shift.patient_name} (
                                                        {formatDateAndTimeWithWeekday(
                                                            shift.shift_start_time,
                                                            displayTimezone,
                                                        )}
                                                        –
                                                        {formatTime(
                                                            shift.shift_end_time,
                                                            displayTimezone,
                                                        )}
                                                        )
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground">
                                            Overlapping assignments
                                        </div>
                                        <ul className="list-disc pl-5">
                                            {conflict.assignments
                                                .filter(
                                                    (assignment) =>
                                                        assignment.caregiverId ===
                                                        caregiverId,
                                                )
                                                .map((assignment) => (
                                                    <li
                                                        key={`${assignment.requestedShiftId}:${assignment.conflictingShiftId}`}
                                                    >
                                                        {assignment.patientName}{' '}
                                                        (
                                                        {formatDateAndTimeWithWeekday(
                                                            assignment.shiftStartTime,
                                                            displayTimezone,
                                                        )}
                                                        –
                                                        {formatTime(
                                                            assignment.shiftEndTime,
                                                            displayTimezone,
                                                        )}
                                                        )
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                </div>
                            ),
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={conflict === null || isSubmitting}
                        onClick={() => {
                            if (conflict) {
                                onConfirm({
                                    ...conflict.acknowledgedConflicts,
                                });
                            }
                        }}
                    >
                        {isSubmitting ? 'Starting…' : 'Start anyway'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
